/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2024, Zumper
 * @description constants.ts
 */

import { BuildingAmenity, ListingAmenity } from '@zcode/enums'

export const combinePrioritiesArrays = (
  mainArray: readonly AmenityPriorityElement[],
  baseArray = AFR_AMENITIES_PRIORITIES
) => {
  const uniqueArraysSet = new Set<string>()
  return [...mainArray, ...baseArray].filter((item) => {
    const { amenities, amenity } = item

    if (amenities?.length) {
      for (const { amenity } of amenities) {
        if (!uniqueArraysSet.has(amenity)) {
          uniqueArraysSet.add(amenity)
        }
      }
    } else if (amenity) {
      if (!uniqueArraysSet.has(amenity)) {
        uniqueArraysSet.add(amenity)
        return true
      }
      return false
    }
    return true
  })
}

interface PriorityListingAmenityDetails {
  amenities?: never
  amenity: string
  id: ListingAmenity
  type: 'listing'
}
interface PriorityBuildingAmenityDetails {
  amenities?: never
  amenity: string
  id: BuildingAmenity
  type: 'building'
}

interface PriorityPetsAmenityDetails {
  amenities?: never
  amenity: string
  id: 'dogs' | 'cats'
  type: 'pets'
}

type PriorityAmenityDetails =
  | PriorityBuildingAmenityDetails
  | PriorityListingAmenityDetails
  | PriorityPetsAmenityDetails

interface PriorityAmenityDetailsGroup {
  amenities: readonly (PriorityBuildingAmenityDetails | PriorityListingAmenityDetails)[]
  amenity?: never
  id?: never
  type?: never
}
type AmenityPriorityElement = PriorityAmenityDetails | PriorityAmenityDetailsGroup

export const AFR_AMENITIES_PRIORITIES: readonly AmenityPriorityElement[] = [
  { amenity: 'Dogs OK', type: 'pets', id: 'dogs' },
  { amenity: 'Cats OK', type: 'pets', id: 'cats' },
  { amenity: 'In Unit Laundry', type: 'listing', id: ListingAmenity.IN_UNIT_LAUNDRY },
  { amenity: 'Furnished', type: 'listing', id: ListingAmenity.FURNISHED },
  { amenity: 'Air Conditioning', type: 'listing', id: ListingAmenity.AIR_CONDITIONING },
  { amenity: 'Garage Parking', type: 'building', id: BuildingAmenity.GARAGE_PARKING },
  { amenity: 'On Site Laundry', type: 'listing', id: ListingAmenity.ON_SITE_LAUNDRY },
  { amenity: 'Balcony', type: 'listing', id: ListingAmenity.BALCONY },
  { amenity: 'Assigned Parking', type: 'listing', id: ListingAmenity.ASSIGNED_PARKING },
  { amenity: 'Swimming Pool', type: 'building', id: BuildingAmenity.SWIMMING_POOL },
  { amenity: 'Central Heat', type: 'listing', id: ListingAmenity.CENTRAL_HEAT },
  { amenity: 'Fitness Center', type: 'building', id: BuildingAmenity.FITNESS_CENTER },
  { amenity: 'Hardwood Floor', type: 'listing', id: ListingAmenity.HARDWOOD_FLOOR },
  { amenity: 'Elevator', type: 'building', id: BuildingAmenity.ELEVATOR },
  { amenity: 'Walk In Closets', type: 'listing', id: ListingAmenity.WALK_IN_CLOSETS },
  { amenity: 'Ceiling Fan', type: 'listing', id: ListingAmenity.CEILING_FAN },
  { amenity: 'Storage', type: 'building', id: BuildingAmenity.STORAGE },
  { amenity: 'Fireplace', type: 'listing', id: ListingAmenity.FIREPLACE },
  { amenity: 'Deck', type: 'listing', id: ListingAmenity.DECK },
  { amenity: 'High Ceilings', type: 'listing', id: ListingAmenity.HIGH_CEILINGS },
  { amenity: 'Controlled Access', type: 'building', id: BuildingAmenity.CONTROLLED_ACCESS },
  { amenity: 'Wheelchair Accessible', type: 'building', id: BuildingAmenity.WHEELCHAIR_ACCESSIBLE },
  { amenity: 'TV', type: 'listing', id: ListingAmenity.TV },
  { amenity: 'Garden', type: 'listing', id: ListingAmenity.GARDEN },
  { amenity: 'Carpet', type: 'listing', id: ListingAmenity.CARPET },
  { amenity: 'EV Charging', type: 'building', id: BuildingAmenity.EV_CHARGING },
  { amenity: 'Concierge Service', type: 'building', id: BuildingAmenity.CONCIERGE_SERVICE },
  { amenity: 'Onsite Management', type: 'building', id: BuildingAmenity.ONSITE_MANAGEMENT },
  { amenity: 'Roof Deck', type: 'building', id: BuildingAmenity.ROOF_DECK },
  { amenity: 'Door Person', type: 'building', id: BuildingAmenity.DOOR_PERSON },
  { amenity: 'Package Service', type: 'building', id: BuildingAmenity.PACKAGE_SERVICE },
  { amenity: 'Residents Lounge', type: 'building', id: BuildingAmenity.RESIDENTS_LOUNGE },
  { amenity: 'Business Center', type: 'building', id: BuildingAmenity.BUSINESS_CENTER },
  { amenity: 'Dry Cleaning Service', type: 'building', id: BuildingAmenity.DRY_CLEANING_SERVICE },
  { amenity: 'Leed Certified', type: 'building', id: BuildingAmenity.LEED_CERTIFIED },
  { amenity: 'Dishwasher', type: 'listing', id: ListingAmenity.DISHWASHER },
  { amenity: 'Outdoor Space', type: 'building', id: BuildingAmenity.OUTDOOR_SPACE },
]

export const HFR_AMENITIES_PRIORITIES: readonly AmenityPriorityElement[] = [
  {
    amenities: [
      { amenity: 'In Unit Laundry', type: 'listing', id: ListingAmenity.IN_UNIT_LAUNDRY },
      { amenity: 'On Site Laundry', type: 'listing', id: ListingAmenity.ON_SITE_LAUNDRY },
    ],
  },
  {
    amenities: [
      { amenity: 'Outdoor Space', type: 'building', id: BuildingAmenity.OUTDOOR_SPACE },
      { amenity: 'Garden', type: 'listing', id: ListingAmenity.GARDEN },
      { amenity: 'Deck', type: 'listing', id: ListingAmenity.DECK },
    ],
  },
  { amenity: 'Storage', type: 'building', id: BuildingAmenity.STORAGE },
  {
    amenities: [
      { amenity: 'Garage Parking', type: 'building', id: BuildingAmenity.GARAGE_PARKING },
      { amenity: 'Assigned Parking', type: 'listing', id: ListingAmenity.ASSIGNED_PARKING },
    ],
  },
  { amenity: 'Air Conditioning', type: 'listing', id: ListingAmenity.AIR_CONDITIONING },
  { amenity: 'Central Heat', type: 'listing', id: ListingAmenity.CENTRAL_HEAT },
  { amenity: 'Swimming Pool', type: 'building', id: BuildingAmenity.SWIMMING_POOL },
  { amenity: 'High Ceilings', type: 'listing', id: ListingAmenity.HIGH_CEILINGS },
  {
    amenities: [
      { amenity: 'Carpet', type: 'listing', id: ListingAmenity.CARPET },
      { amenity: 'Hardwood Floor', type: 'listing', id: ListingAmenity.HARDWOOD_FLOOR },
    ],
  },
  { amenity: 'Dogs OK', type: 'pets', id: 'dogs' },
  { amenity: 'Cats OK', type: 'pets', id: 'cats' },
  { amenity: 'Fireplace', type: 'listing', id: ListingAmenity.FIREPLACE },
  { amenity: 'Furnished', type: 'listing', id: ListingAmenity.FURNISHED },
  { amenity: 'Furnished', type: 'listing', id: ListingAmenity.FURNISHED },
  { amenity: 'Balcony', type: 'listing', id: ListingAmenity.BALCONY },
  { amenity: 'Fitness Center', type: 'building', id: BuildingAmenity.FITNESS_CENTER },
  { amenity: 'Elevator', type: 'building', id: BuildingAmenity.ELEVATOR },
  { amenity: 'Walk In Closets', type: 'listing', id: ListingAmenity.WALK_IN_CLOSETS },
  { amenity: 'Ceiling Fan', type: 'listing', id: ListingAmenity.CEILING_FAN },
  { amenity: 'Fireplace', type: 'listing', id: ListingAmenity.FIREPLACE },
  { amenity: 'Controlled Access', type: 'building', id: BuildingAmenity.CONTROLLED_ACCESS },
  { amenity: 'Wheelchair Accessible', type: 'building', id: BuildingAmenity.WHEELCHAIR_ACCESSIBLE },
  { amenity: 'TV', type: 'listing', id: ListingAmenity.TV },
  { amenity: 'EV Charging', type: 'building', id: BuildingAmenity.EV_CHARGING },
  { amenity: 'Concierge Service', type: 'building', id: BuildingAmenity.CONCIERGE_SERVICE },
  { amenity: 'Onsite Management', type: 'building', id: BuildingAmenity.ONSITE_MANAGEMENT },
  { amenity: 'Roof Deck', type: 'building', id: BuildingAmenity.ROOF_DECK },
  { amenity: 'Door Person', type: 'building', id: BuildingAmenity.DOOR_PERSON },
  { amenity: 'Package Service', type: 'building', id: BuildingAmenity.PACKAGE_SERVICE },
  { amenity: 'Residents Lounge', type: 'building', id: BuildingAmenity.RESIDENTS_LOUNGE },
  { amenity: 'Business Center', type: 'building', id: BuildingAmenity.BUSINESS_CENTER },
  { amenity: 'Dry Cleaning Service', type: 'building', id: BuildingAmenity.DRY_CLEANING_SERVICE },
  { amenity: 'Leed Certified', type: 'building', id: BuildingAmenity.LEED_CERTIFIED },
  { amenity: 'Dishwasher', type: 'listing', id: ListingAmenity.DISHWASHER },
] as const

const PET_FRIENDLY_AMENITIES_PRIORITIES_ARRAY: readonly AmenityPriorityElement[] = [
  {
    amenities: [
      { amenity: 'Outdoor Space', type: 'building', id: BuildingAmenity.OUTDOOR_SPACE },
      { amenity: 'Garden', type: 'listing', id: ListingAmenity.GARDEN },
    ],
  },
  {
    amenities: [
      { amenity: 'Carpet', type: 'listing', id: ListingAmenity.CARPET },
      { amenity: 'Hardwood Floor', type: 'listing', id: ListingAmenity.HARDWOOD_FLOOR },
    ],
  },
  {
    amenities: [
      { amenity: 'In Unit Laundry', type: 'listing', id: ListingAmenity.IN_UNIT_LAUNDRY },
      { amenity: 'On Site Laundry', type: 'listing', id: ListingAmenity.ON_SITE_LAUNDRY },
    ],
  },
  { amenity: 'Storage', type: 'building', id: BuildingAmenity.STORAGE },
] as const

export const PET_FRIENDLY_AMENITIES_PRIORITIES = combinePrioritiesArrays(
  PET_FRIENDLY_AMENITIES_PRIORITIES_ARRAY
)

export const PET_FRIENDLY_AMENITIES_PRIORITIES_HFR = combinePrioritiesArrays(
  PET_FRIENDLY_AMENITIES_PRIORITIES_ARRAY,
  HFR_AMENITIES_PRIORITIES
)

const LUXURY_AMENITIES_PRIORITIES_ARRAY = [
  { amenity: 'Balcony', type: 'listing', id: ListingAmenity.BALCONY },
  { amenity: 'Roof Deck', type: 'building', id: BuildingAmenity.ROOF_DECK },
  { amenity: 'Walk In Closets', type: 'listing', id: ListingAmenity.WALK_IN_CLOSETS },
  { amenity: 'Swimming Pool', type: 'building', id: BuildingAmenity.SWIMMING_POOL },
  { amenity: 'Fitness Center', type: 'building', id: BuildingAmenity.FITNESS_CENTER },
  { amenity: 'In Unit Laundry', type: 'listing', id: ListingAmenity.IN_UNIT_LAUNDRY },
  { amenity: 'Controlled Access', type: 'building', id: BuildingAmenity.CONTROLLED_ACCESS },
  { amenity: 'Door person ', type: 'building', id: BuildingAmenity.DOOR_PERSON },
  { amenity: 'Dry Cleaning Service ', type: 'building', id: BuildingAmenity.DRY_CLEANING_SERVICE },
  { amenity: 'EV charging', type: 'building', id: BuildingAmenity.EV_CHARGING },
] as const

export const LUXURY_AMENITIES_PRIORITIES = combinePrioritiesArrays(
  LUXURY_AMENITIES_PRIORITIES_ARRAY
)

export const LUXURY_AMENITIES_PRIORITIES_HFR = combinePrioritiesArrays(
  LUXURY_AMENITIES_PRIORITIES_ARRAY,
  HFR_AMENITIES_PRIORITIES
)

const CHEAP_AMENITIES_PRIORITIES_ARRAY = [
  {
    amenities: [
      { amenity: 'In Unit Laundry', type: 'listing', id: ListingAmenity.IN_UNIT_LAUNDRY },
      { amenity: 'On Site Laundry', type: 'listing', id: ListingAmenity.ON_SITE_LAUNDRY },
    ],
  },
  {
    amenities: [
      { amenity: 'Garage Parking', type: 'building', id: BuildingAmenity.GARAGE_PARKING },
      { amenity: 'Assigned Parking', type: 'listing', id: ListingAmenity.ASSIGNED_PARKING },
    ],
  },
  { amenity: 'Dishwasher', type: 'listing', id: ListingAmenity.DISHWASHER },
  { amenity: 'Storage', type: 'building', id: BuildingAmenity.STORAGE },
  { amenity: 'Onsite Management', type: 'building', id: BuildingAmenity.ONSITE_MANAGEMENT },
  {
    amenities: [
      { amenity: 'Carpet', type: 'listing', id: ListingAmenity.CARPET },
      { amenity: 'Hardwood Floor', type: 'listing', id: ListingAmenity.HARDWOOD_FLOOR },
    ],
  },
  { amenity: 'Central Heat', type: 'listing', id: ListingAmenity.CENTRAL_HEAT },
  { amenity: 'Air Conditioning', type: 'listing', id: ListingAmenity.AIR_CONDITIONING },
  { amenity: 'Dogs OK', type: 'pets', id: 'dogs' },
  { amenity: 'Cats OK', type: 'pets', id: 'cats' },
] as const

export const CHEAP_AMENITIES_PRIORITIES = combinePrioritiesArrays(CHEAP_AMENITIES_PRIORITIES_ARRAY)

export const CHEAP_AMENITIES_PRIORITIES_HFR = combinePrioritiesArrays(
  CHEAP_AMENITIES_PRIORITIES_ARRAY,
  HFR_AMENITIES_PRIORITIES
)

const UTILITIES_INCLUDED_AMENITIES_PRIORITIES_ARRAY = [
  { amenity: 'In Unit Laundry', type: 'listing', id: ListingAmenity.IN_UNIT_LAUNDRY },
  { amenity: 'Dishwasher', type: 'listing', id: ListingAmenity.DISHWASHER },
  { amenity: 'Air Conditioning', type: 'listing', id: ListingAmenity.AIR_CONDITIONING },
  { amenity: 'Central Heat', type: 'listing', id: ListingAmenity.CENTRAL_HEAT },
  { amenity: 'Fitness Center', type: 'building', id: BuildingAmenity.FITNESS_CENTER },
] as const

export const UTILITIES_INCLUDED_AMENITIES_PRIORITIES = combinePrioritiesArrays(
  UTILITIES_INCLUDED_AMENITIES_PRIORITIES_ARRAY
)

export const UTILITIES_INCLUDED_AMENITIES_PRIORITIES_HFR = combinePrioritiesArrays(
  UTILITIES_INCLUDED_AMENITIES_PRIORITIES_ARRAY,
  HFR_AMENITIES_PRIORITIES
)

const FURNISHED_AMENITIES_PRIORITIES_ARRAY = [
  { amenity: 'Storage', type: 'building', id: BuildingAmenity.STORAGE },
  { amenity: 'Walk In Closets', type: 'listing', id: ListingAmenity.WALK_IN_CLOSETS },
  {
    amenities: [
      { amenity: 'In Unit Laundry', type: 'listing', id: ListingAmenity.IN_UNIT_LAUNDRY },
      { amenity: 'On Site Laundry', type: 'listing', id: ListingAmenity.ON_SITE_LAUNDRY },
    ],
  },
  { amenity: 'Dishwasher', type: 'listing', id: ListingAmenity.DISHWASHER },
  { amenity: 'Dry Cleaning Service', type: 'building', id: BuildingAmenity.DRY_CLEANING_SERVICE },
  { amenity: 'Business Center', type: 'building', id: BuildingAmenity.BUSINESS_CENTER },
  { amenity: 'Fitness Center', type: 'building', id: BuildingAmenity.FITNESS_CENTER },
  {
    amenities: [
      { amenity: 'Garage Parking', type: 'building', id: BuildingAmenity.GARAGE_PARKING },
      { amenity: 'Assigned Parking', type: 'listing', id: ListingAmenity.ASSIGNED_PARKING },
    ],
  },
] as const

export const FURNISHED_AMENITIES_PRIORITIES = combinePrioritiesArrays(
  FURNISHED_AMENITIES_PRIORITIES_ARRAY
)

export const FURNISHED_AMENITIES_PRIORITIES_HFR = combinePrioritiesArrays(
  FURNISHED_AMENITIES_PRIORITIES_ARRAY,
  HFR_AMENITIES_PRIORITIES
)
